import { foldContentLine } from '@/lib/foldContentLine'

export type SavableContact = Readonly<{
  firstName: string
  lastName: string | null
  phoneNumber: string | null
  emailAddress: string | null
  socialMedia: string | null
  /** Where they were met, for the note: "Madrid outreach (Sat, 24 Oct)". */
  metAt: string | null
}>

/** Text values escape backslash, comma, semicolon and new lines (RFC 6350 3.4). */
function escapeVcard(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;').replace(/\r?\n/g, '\\n')
}

/**
 * The person as a contact card (vCard 3.0, which iPhone and Android contacts
 * both open): their name, mobile, email, and a note saying where they were
 * met and their social media. Nothing else from the registration goes in.
 */
export function contactVcard(contact: SavableContact): string {
  const first = contact.firstName.trim()
  const last = contact.lastName?.trim() ?? ''
  const full = [first, last].filter(Boolean).join(' ')
  const phone = contact.phoneNumber?.replace(/[^\d+]/g, '') ?? ''
  const note = [
    contact.metAt ? `Met at ${contact.metAt}` : null,
    contact.socialMedia?.trim() ? `Social media: ${contact.socialMedia.trim()}` : null,
  ].filter(Boolean).join('\n')
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${escapeVcard(last)};${escapeVcard(first)};;;`,
    `FN:${escapeVcard(full || first)}`,
    ...(phone ? [`TEL;TYPE=CELL:${phone}`] : []),
    ...(contact.emailAddress?.trim() ? [`EMAIL;TYPE=INTERNET:${escapeVcard(contact.emailAddress.trim())}`] : []),
    ...(note ? [`NOTE:${escapeVcard(note)}`] : []),
    'END:VCARD',
    '',
  ]
  return lines.map(foldContentLine).join('\r\n')
}

/** "Ada Lovelace.vcf": the name the phone shows when it offers to add it. */
export function contactFileName(contact: Pick<SavableContact, 'firstName' | 'lastName'>): string {
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ').replace(/[\\/:*?"<>|]+/g, '').trim()
  return `${name || 'Contact'}.vcf`
}

/**
 * Downloads the contact card. An iPhone opens it straight into Create New
 * Contact; Android opens the downloaded file in Contacts. (The share sheet
 * often doesn't offer Contacts for a .vcf, so it isn't used.)
 */
export function saveContactToPhone(contact: SavableContact): void {
  const blob = new Blob([contactVcard(contact)], { type: 'text/vcard;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = contactFileName(contact)
  document.body.append(link)
  link.click()
  link.remove()
  // Let the browser begin the download before releasing the temporary URL.
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}
